import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import {
  Archive,
  ArrowUpRight,
  Box,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  File,
  FileText,
  Folder,
  Globe,
  Grid2X2,
  ImageIcon,
  Loader2,
  MessageCircle,
  MoreHorizontal,
  PanelsTopLeft,
  RefreshCw,
  Search,
  Send,
  Table2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { buildAgentFileURL } from '@/utils/agent-file-url';
import { cn } from '@/utils/cn';
import { Button } from '../components/ui/button';
import { getWebsiteProfile } from '../lib/auth';
import { chatConversationPath } from '../lib/conversation-core';
import {
  getChatBootstrap,
  HTTPStatusError,
  listAgentConversationPage,
  type WebAgentConversationInfo,
} from '../lib/chat-api';
import {
  getBookFileIconKind,
  getBookFileKindLabel,
  getBookFileName,
  getBookVersionArtifactKindLabel,
  getBookVersionArtifactSystemIcon,
  getBookVersionModifiedAt,
  getPrimaryBookFile,
  getProjectBookDisplayTitle,
  getProjectBookLastChangedAt,
  getProjectBookLatestVersion,
  isBookVersionWebsite,
  isPreviewableTextFile,
  listAgentBooks,
  projectBooksFromAgentBooks,
  readAgentBookFileText,
  type ProjectBook,
  type ProjectBookFile,
  type ProjectBookVersion,
  type ProjectBookVersionSessionLink,
} from '../lib/book';

const bookPageBackground = 'bg-[#fafafa] dark:bg-background';
const searchFieldClassName =
  'h-[34px] w-[156px] rounded-[10px] border border-black/8 bg-black/[0.035] px-3 text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-black/10 focus:bg-black/[0.055] dark:border-white/8 dark:bg-white/[0.06] dark:focus:bg-white/10';
const websitePreviewStyle =
  'html,body,body *{cursor:default!important;pointer-events:none!important;-webkit-user-select:none!important;user-select:none!important}html,body{overflow:hidden!important}';

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof HTTPStatusError) {
    return error.status;
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return error.status;
  }
  return undefined;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

async function readPreviewText(filePath: string): Promise<string> {
  return readPreviewTextAttempt(filePath, 0);
}

async function readPreviewTextAttempt(
  filePath: string,
  attempt: number
): Promise<string> {
  try {
    return await readAgentBookFileText(filePath);
  } catch (error) {
    if (attempt >= 2) {
      throw error;
    }
    await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
    return readPreviewTextAttempt(filePath, attempt + 1);
  }
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 bytes';
  }
  const units = ['bytes', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const digits = unitIndex === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
}

function relativeChangeTime(timestamp: number): string {
  if (timestamp <= 0) {
    return 'Updated recently';
  }

  const elapsedSeconds = Math.max(0, Date.now() / 1000 - timestamp);
  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const month = day * 30;
  const year = day * 365;

  if (elapsedSeconds < minute) {
    return 'Just now';
  }
  if (elapsedSeconds < hour) {
    return `${Math.max(1, Math.floor(elapsedSeconds / minute))}m ago`;
  }
  if (elapsedSeconds < day) {
    return `${Math.max(1, Math.floor(elapsedSeconds / hour))}h ago`;
  }
  if (elapsedSeconds < week) {
    return `${Math.max(1, Math.floor(elapsedSeconds / day))}d ago`;
  }
  if (elapsedSeconds < month) {
    return `${Math.max(1, Math.floor(elapsedSeconds / week))}w ago`;
  }
  if (elapsedSeconds < year) {
    return `${Math.max(1, Math.floor(elapsedSeconds / month))}mo ago`;
  }
  return `${Math.max(1, Math.floor(elapsedSeconds / year))}y ago`;
}

function normalizeSearch(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function selectedVersionFor(
  book: ProjectBook | null,
  selectedVersionID: string | null
): ProjectBookVersion | null {
  if (!book) {
    return null;
  }
  if (selectedVersionID) {
    const selected = book.versions.find(
      version => version.id === selectedVersionID
    );
    if (selected) {
      return selected;
    }
  }
  return getProjectBookLatestVersion(book);
}

function selectedFileFor(
  version: ProjectBookVersion | null,
  selectedFileID: string | null
): ProjectBookFile | null {
  if (!version) {
    return null;
  }
  if (selectedFileID) {
    const selected = version.files.find(file => file.id === selectedFileID);
    if (selected) {
      return selected;
    }
  }
  return getPrimaryBookFile(version);
}

function bookFileDirectoryURL(filePath: string): string {
  const normalizedPath = filePath.trim().replace(/\\/g, '/');
  const directoryPath = normalizedPath.includes('/')
    ? normalizedPath.slice(0, normalizedPath.lastIndexOf('/'))
    : '';
  const directoryURL = buildAgentFileURL(directoryPath || '/');
  return directoryURL.endsWith('/') ? directoryURL : `${directoryURL}/`;
}

function rewriteRootRelativePreviewURL(value: string, baseURL: string): string {
  const trimmed = value.trim();
  if (
    !trimmed.startsWith('/') ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('/v1/')
  ) {
    return value;
  }
  return `${baseURL}${trimmed.slice(1)}`;
}

function rewritePreviewSrcset(value: string, baseURL: string): string {
  return value
    .split(',')
    .map(candidate => {
      const trimmed = candidate.trim();
      if (!trimmed) {
        return candidate;
      }
      const [url, ...rest] = trimmed.split(/\s+/);
      return [rewriteRootRelativePreviewURL(url, baseURL), ...rest].join(' ');
    })
    .join(', ');
}

function rewritePreviewCSSURLs(value: string, baseURL: string): string {
  return value.replace(
    /url\((['"]?)(\/(?!\/|v1\/)[^)'"\\]+)\1\)/g,
    (_match, quote: string, path: string) =>
      `url(${quote}${baseURL}${path.slice(1)}${quote})`
  );
}

function websitePreviewDocument(html: string, filePath: string): string {
  if (!html.trim()) {
    return '';
  }
  if (typeof DOMParser === 'undefined') {
    return html;
  }

  const baseURL = bookFileDirectoryURL(filePath);
  const document = new DOMParser().parseFromString(html, 'text/html');
  const head = document.head ?? document.createElement('head');
  if (!document.head) {
    document.documentElement.prepend(head);
  }

  const base = document.createElement('base');
  base.href = baseURL;
  head.prepend(base);

  const style = document.createElement('style');
  style.id = 'cueboard-book-cover-static-preview';
  style.textContent = websitePreviewStyle;
  head.append(style);

  for (const element of document.querySelectorAll('[src], [href], [poster]')) {
    for (const attribute of ['src', 'href', 'poster']) {
      const value = element.getAttribute(attribute);
      if (value) {
        element.setAttribute(
          attribute,
          rewriteRootRelativePreviewURL(value, baseURL)
        );
      }
    }
  }

  for (const element of document.querySelectorAll('[srcset]')) {
    const value = element.getAttribute('srcset');
    if (value) {
      element.setAttribute('srcset', rewritePreviewSrcset(value, baseURL));
    }
  }

  for (const element of document.querySelectorAll('[style]')) {
    const value = element.getAttribute('style');
    if (value) {
      element.setAttribute('style', rewritePreviewCSSURLs(value, baseURL));
    }
  }

  for (const element of document.querySelectorAll('style')) {
    element.textContent = rewritePreviewCSSURLs(
      element.textContent ?? '',
      baseURL
    );
  }

  return `<!doctype html>\n${document.documentElement.outerHTML}`;
}

function versionMenuTitle(
  version: ProjectBookVersion,
  isLatest: boolean
): string {
  const pieces = [isLatest ? 'Latest version' : version.label];
  pieces.push(
    `${version.files.length} ${version.files.length === 1 ? 'file' : 'files'}`
  );
  if (isBookVersionWebsite(version)) {
    pieces.push('website');
  }
  return pieces.join(' · ');
}

function firstConversationLink(
  links: ProjectBookVersionSessionLink[]
): ProjectBookVersionSessionLink | null {
  return (
    [...links].reverse().find(link => link.sessionID.trim().length > 0) ?? null
  );
}

function conversationMatchesRuntimeSession(
  conversation: WebAgentConversationInfo,
  sessionID: string
): boolean {
  const normalizedSessionID = sessionID.trim();
  if (!normalizedSessionID) {
    return false;
  }

  return (
    conversation.conversationId.trim() === normalizedSessionID ||
    conversation.createdBySessionId?.trim() === normalizedSessionID ||
    (conversation.participants ?? []).some(
      participant => participant.sessionId?.trim() === normalizedSessionID
    )
  );
}

type ResolvedConversationTarget = {
  conversationID: string;
  agentGroupID: string | null;
};

function conversationIDForRuntimeSessionMatch(
  conversation: WebAgentConversationInfo,
  sessionID: string
): string {
  const normalizedSessionID = sessionID.trim();
  const conversationID = conversation.conversationId.trim();
  const runtimeParticipant = (conversation.participants ?? []).find(
    participant => participant.sessionId?.trim() === normalizedSessionID
  );
  const participantAgentID =
    runtimeParticipant?.agentId?.trim() ??
    conversation.createdByAgentId.trim();
  if (
    normalizedSessionID &&
    participantAgentID &&
    (!conversationID || conversationID === normalizedSessionID)
  ) {
    return `${participantAgentID}:${normalizedSessionID}`;
  }
  return conversationID;
}

async function resolveConversationTargetForRuntimeSession(
  sessionID: string
): Promise<ResolvedConversationTarget | null> {
  const normalizedSessionID = sessionID.trim();
  if (!normalizedSessionID) {
    return null;
  }

  const bootstrap = await getChatBootstrap();
  return resolveConversationTargetForRuntimeSessionPage({
    agentGroupId: bootstrap.agent.agent_group_id,
    sessionID: normalizedSessionID,
    cursor: null,
    pageIndex: 0,
  });
}

async function resolveConversationTargetForRuntimeSessionPage({
  agentGroupId,
  sessionID,
  cursor,
  pageIndex,
}: {
  agentGroupId: string;
  sessionID: string;
  cursor: string | null;
  pageIndex: number;
}): Promise<ResolvedConversationTarget | null> {
  if (pageIndex >= 10) {
    return null;
  }

  const page = await listAgentConversationPage({
    agentGroupId,
    limit: 100,
    cursor,
  });
  for (const conversation of page.data) {
    if (!conversationMatchesRuntimeSession(conversation, sessionID)) {
      continue;
    }
    const conversationID = conversationIDForRuntimeSessionMatch(
      conversation,
      sessionID
    );
    if (conversationID) {
      return {
        conversationID,
        agentGroupID: conversation.agentGroupId ?? agentGroupId,
      };
    }
  }
  if (!page.hasMore || !page.nextCursor) {
    return null;
  }
  return resolveConversationTargetForRuntimeSessionPage({
    agentGroupId,
    sessionID,
    cursor: page.nextCursor,
    pageIndex: pageIndex + 1,
  });
}

function iconForKind(kind: string): ComponentType<{ className?: string }> {
  switch (kind) {
    case 'archive':
      return Archive;
    case 'file-text':
      return FileText;
    case 'folder':
      return Folder;
    case 'globe':
      return Globe;
    case 'grid':
      return Grid2X2;
    case 'image':
      return ImageIcon;
    case 'panels':
      return PanelsTopLeft;
    case 'table':
      return Table2;
    default:
      return File;
  }
}

function BookKindIcon({
  kind,
  className,
}: {
  kind: string;
  className?: string;
}) {
  const Icon = iconForKind(kind);
  return <Icon className={className} />;
}

function BookSearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="relative block">
      <Search className="pointer-events-none absolute top-1/2 left-3 size-[13px] -translate-y-1/2 text-muted-foreground" />
      <input
        className={cn(searchFieldClassName, 'pl-8')}
        value={value}
        placeholder="Search"
        onChange={event => onChange(event.target.value)}
        aria-label="Search Books"
      />
    </label>
  );
}

function BookPreview({
  version,
  file,
  previewText,
  presentation,
}: {
  version: ProjectBookVersion | null;
  file: ProjectBookFile | null;
  previewText?: string;
  presentation: 'gallery' | 'detail';
}) {
  const isWebsite = isBookVersionWebsite(version);
  const fileKind = file ? getBookFileKindLabel(file) : '';
  const fileURL = file ? buildAgentFileURL(file.path) : '';
  const websiteScale = presentation === 'detail' ? 0.197 : 0.255;
  const frameClass =
    presentation === 'detail'
      ? 'h-[146px] w-full max-w-[252px] rounded-md'
      : 'h-full w-full rounded-md';

  if (isWebsite && file) {
    const previewDocument = previewText?.trim()
      ? websitePreviewDocument(previewText, file.path)
      : '';
    if (!previewDocument) {
      return (
        <BookKindIcon
          kind={getBookVersionArtifactSystemIcon(version)}
          className="size-7 text-muted-foreground"
        />
      );
    }

    return (
      <div
        className={cn(
          frameClass,
          'pointer-events-none flex items-center justify-center overflow-hidden border border-black/10 bg-white'
        )}
      >
        <div
          style={{
            width: 1280 * websiteScale,
            height: 740 * websiteScale,
          }}
        >
          <iframe
            className="h-[740px] w-[1280px] origin-top-left border-0 bg-white"
            style={{ transform: `scale(${websiteScale})` }}
            srcDoc={previewDocument}
            title={version?.title ?? 'Website preview'}
            sandbox="allow-same-origin"
            tabIndex={-1}
            aria-hidden={presentation === 'gallery'}
          />
        </div>
      </div>
    );
  }

  if (file && fileKind === 'Image') {
    return (
      <img
        className={cn(
          frameClass,
          'pointer-events-none object-contain',
          presentation === 'gallery'
            ? 'p-2'
            : 'shadow-[0_10px_22px_rgba(0,0,0,0.12)]'
        )}
        src={fileURL}
        alt={getBookFileName(file)}
      />
    );
  }

  if (file && fileKind === 'PDF') {
    return (
      <iframe
        className={cn(
          presentation === 'detail'
            ? 'h-[146px] w-[112px] rounded-md shadow-[0_10px_22px_rgba(0,0,0,0.14)]'
            : 'mx-auto h-full w-[42%] rounded-md shadow-[0_8px_18px_rgba(0,0,0,0.12)]',
          'pointer-events-none border border-black/10 bg-white'
        )}
        src={fileURL}
        title={getBookFileName(file)}
        tabIndex={-1}
        aria-hidden={presentation === 'gallery'}
      />
    );
  }

  if (previewText?.trim()) {
    return (
      <pre
        className={cn(
          frameClass,
          'pointer-events-none overflow-hidden whitespace-pre-wrap rounded-md border border-black/10 bg-white p-2 text-left font-mono text-[8px] leading-[1.35] text-foreground/78 dark:bg-white/[0.04]'
        )}
      >
        {previewText}
      </pre>
    );
  }

  if (file) {
    return (
      <div
        className={cn(
          frameClass,
          'pointer-events-none flex flex-col items-center justify-center gap-2 border border-black/10 bg-white text-center dark:bg-white/[0.04]'
        )}
      >
        <BookKindIcon
          kind={getBookFileIconKind(file)}
          className="size-[30px] text-muted-foreground"
        />
        <div className="max-w-[80%] space-y-0.5">
          <div className="truncate text-xs font-semibold text-foreground">
            {getBookFileName(file)}
          </div>
          <div className="truncate text-[10px] text-muted-foreground">
            {getBookFileKindLabel(file)} · {formatBytes(file.size)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <BookKindIcon
      kind={getBookVersionArtifactSystemIcon(version)}
      className="size-7 text-muted-foreground"
    />
  );
}

function BookGalleryCard({
  book,
  onOpen,
}: {
  book: ProjectBook;
  onOpen: (book: ProjectBook) => void;
}) {
  const latestVersion = getProjectBookLatestVersion(book);
  const primaryFile = getPrimaryBookFile(latestVersion);
  const kindLabel = getBookVersionArtifactKindLabel(latestVersion);
  const [websitePreviewHTML, setWebsitePreviewHTML] = useState('');
  const websitePreviewKey = [
    latestVersion?.id ?? '',
    primaryFile?.id ?? '',
    primaryFile?.modifiedAt ?? 0,
  ].join('|');

  useEffect(() => {
    let cancelled = false;
    async function loadWebsitePreview() {
      setWebsitePreviewHTML('');
      if (
        !latestVersion ||
        !primaryFile ||
        !isBookVersionWebsite(latestVersion) ||
        !isPreviewableTextFile(primaryFile)
      ) {
        return;
      }

      try {
        const html = await readPreviewText(primaryFile.path);
        if (!cancelled) {
          setWebsitePreviewHTML(html);
        }
      } catch {
        if (!cancelled) {
          setWebsitePreviewHTML('');
        }
      }
    }

    void loadWebsitePreview();
    return () => {
      cancelled = true;
    };
  }, [latestVersion, primaryFile, websitePreviewKey]);

  return (
    <button
      type="button"
      className="group w-full rounded-lg p-2 text-left transition-[background,box-shadow] hover:bg-black/[0.06] hover:shadow-[0_8px_18px_rgba(0,0,0,0.10)] dark:hover:bg-white/[0.06]"
      onClick={() => onOpen(book)}
    >
      <div className="flex h-[170px] items-center justify-center overflow-hidden rounded-md border border-black/[0.09] bg-[color:var(--book-preview-bg,#f3f3f3)] dark:bg-white/[0.04]">
        <BookPreview
          version={latestVersion}
          file={primaryFile}
          previewText={websitePreviewHTML}
          presentation="gallery"
        />
      </div>
      <div className="mt-2 flex items-start gap-2 px-0.5 pb-0.5">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold leading-5 text-foreground">
            {getProjectBookDisplayTitle(book)}
          </div>
          <div className="mt-0.5 truncate text-[13px] text-muted-foreground">
            {kindLabel} ·{' '}
            {relativeChangeTime(getProjectBookLastChangedAt(book))}
          </div>
        </div>
        <MoreHorizontal className="mt-1 size-[18px] shrink-0 text-muted-foreground/75 opacity-70 transition-opacity group-hover:opacity-100" />
      </div>
    </button>
  );
}

function GalleryView({
  books,
  loading,
  error,
  searchText,
  onSearchTextChange,
  onRefresh,
  refreshing,
  onOpenBook,
}: {
  books: ProjectBook[];
  loading: boolean;
  error: string | null;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onRefresh: () => void;
  refreshing: boolean;
  onOpenBook: (book: ProjectBook) => void;
}) {
  const query = normalizeSearch(searchText);
  const visibleBooks = useMemo(
    () =>
      books.filter(book => {
        if (!query) {
          return true;
        }
        return [
          getProjectBookDisplayTitle(book),
          book.title,
          book.project.name,
          book.project.description,
        ].some(value => value.toLocaleLowerCase().includes(query));
      }),
    [books, query]
  );

  return (
    <div className={cn('flex h-full flex-col', bookPageBackground)}>
      <header className="flex h-[60px] shrink-0 items-center gap-4 px-4">
        <h1 className="text-[15px] font-normal leading-none text-foreground">
          Books
        </h1>
        <div className="flex-1" />
        <BookSearchField value={searchText} onChange={onSearchTextChange} />
        <Button
          variant="ghost"
          size="icon-sm"
          className="size-8 rounded-full text-muted-foreground hover:bg-black/[0.06] hover:text-foreground dark:hover:bg-white/[0.06]"
          disabled={loading || refreshing}
          onClick={onRefresh}
          aria-label="Refresh Books"
        >
          <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />
        </Button>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <CenteredState>
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </CenteredState>
        ) : error ? (
          <CenteredState>
            <div className="max-w-[360px] text-center text-sm text-muted-foreground">
              {error}
            </div>
          </CenteredState>
        ) : books.length === 0 ? (
          <CenteredState>
            <div className="text-center">
              <Box className="mx-auto size-8 text-muted-foreground/70" />
              <div className="mt-3 text-sm font-medium text-foreground">
                No Books yet
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Delivered project artifacts will appear here.
              </div>
            </div>
          </CenteredState>
        ) : visibleBooks.length === 0 ? (
          <CenteredState>
            <div className="text-sm text-muted-foreground">
              No matches found.
            </div>
          </CenteredState>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,344px))] items-start justify-start gap-x-4 gap-y-[18px] px-4 pt-0.5 pb-6">
            {visibleBooks.map(book => (
              <BookGalleryCard key={book.id} book={book} onOpen={onOpenBook} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function CenteredState({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[320px] items-center justify-center px-8">
      {children}
    </div>
  );
}

function DetailTopBar({
  title,
  onBack,
  onSendToChat,
}: {
  title: string;
  onBack: () => void;
  onSendToChat: () => void;
}) {
  return (
    <header className="flex h-[60px] shrink-0 items-center gap-2 px-6">
      <div className="flex h-9 overflow-hidden rounded-full border border-black/8 bg-white/72 shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:bg-white/[0.04]">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center text-foreground hover:bg-black/[0.06] dark:hover:bg-white/[0.06]"
          onClick={onBack}
          aria-label="Back to Books"
        >
          <ChevronLeft className="size-[13px]" />
        </button>
        <div className="my-2 w-px bg-border/80" />
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center text-muted-foreground/45"
          disabled
          aria-label="Forward"
        >
          <ChevronRight className="size-[13px]" />
        </button>
      </div>

      <div className="min-w-0 flex-1 px-2">
        <div className="truncate text-[15px] font-normal text-foreground">
          {title}
        </div>
      </div>

      <Button
        variant="ghost"
        className="h-9 rounded-full px-3 text-xs font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-black/[0.06] dark:hover:bg-white/[0.06]"
        onClick={onSendToChat}
      >
        <Send className="size-3.5" />
        Send to Chat
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        className="size-9 rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:bg-black/[0.06] dark:hover:bg-white/[0.06]"
        aria-label="More"
      >
        <MoreHorizontal className="size-[13px]" />
      </Button>
    </header>
  );
}

function DetailRow({
  icon,
  text,
  muted = false,
  trailing,
  onClick,
}: {
  icon: ReactNode;
  text: string;
  muted?: boolean;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
        {icon}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-[13px]',
          muted ? 'text-muted-foreground' : 'text-foreground'
        )}
      >
        {text}
      </span>
      {trailing ? (
        <span className="shrink-0 text-muted-foreground">{trailing}</span>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="flex min-h-9 w-full items-center gap-2 rounded-md px-1 text-left hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return <div className="flex min-h-9 items-center gap-2 px-1">{content}</div>;
}

function WebsiteStatusRow({
  status,
  siteURL,
  onOpenSite,
}: {
  status: 'deployed' | 'undeployed';
  siteURL: string;
  onOpenSite: () => void;
}) {
  return (
    <div className="flex min-h-9 items-center gap-2 px-1">
      <Circle
        className={cn(
          'size-2.5 fill-current',
          status === 'deployed' ? 'text-green-500' : 'text-muted-foreground'
        )}
      />
      <span className="text-[13px] font-semibold text-foreground">
        {status === 'deployed' ? 'Deployed' : 'Undeployed'}
      </span>
      <div className="flex-1" />
      <Button size="sm" className="h-7 rounded-md px-2.5" onClick={onOpenSite}>
        {status === 'deployed' ? 'Open' : 'Deploy'}
      </Button>
      {siteURL ? <span className="sr-only">Site URL: {siteURL}</span> : null}
    </div>
  );
}

function DetailView({
  book,
  selectedVersion,
  selectedFile,
  previewText,
  previewLoading,
  openingConversationSessionID,
  relatedChatError,
  onBack,
  onSendToChat,
  onSelectVersion,
  onSelectFile,
  onOpenFile,
  onOpenConversation,
}: {
  book: ProjectBook;
  selectedVersion: ProjectBookVersion | null;
  selectedFile: ProjectBookFile | null;
  previewText: string;
  previewLoading: boolean;
  openingConversationSessionID: string | null;
  relatedChatError: string;
  onBack: () => void;
  onSendToChat: () => void;
  onSelectVersion: (versionID: string) => void;
  onSelectFile: (fileID: string) => void;
  onOpenFile: (file: ProjectBookFile) => void;
  onOpenConversation: (link: ProjectBookVersionSessionLink) => void;
}) {
  const isWebsite = isBookVersionWebsite(selectedVersion);
  const versionIsLatest =
    selectedVersion?.id === getProjectBookLatestVersion(book)?.id;
  const primaryConversationLink = selectedVersion
    ? firstConversationLink(selectedVersion.sessionLinks)
    : null;
  const openingConversation =
    primaryConversationLink?.sessionID === openingConversationSessionID;
  const siteURL = selectedVersion?.publishedURL.trim() ?? '';
  const siteStatus = siteURL ? 'deployed' : 'undeployed';

  return (
    <div className={cn('flex h-full flex-col', bookPageBackground)}>
      <DetailTopBar
        title={getProjectBookDisplayTitle(book)}
        onBack={onBack}
        onSendToChat={onSendToChat}
      />

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[760px] px-8 pt-2 pb-12">
          <div className="mb-3 flex items-center gap-3">
            <div className="text-[18px] font-semibold text-muted-foreground">
              Status
            </div>
            <div className="flex-1" />
            <label className="relative">
              <select
                className="h-8 min-w-[152px] appearance-none rounded-full border border-black/8 bg-white/72 pr-8 pl-3 text-[13px] font-medium text-foreground shadow-[0_1px_2px_rgba(0,0,0,0.04)] outline-none focus:ring-2 focus:ring-ring/20 dark:bg-white/[0.04]"
                value={selectedVersion?.id ?? ''}
                onChange={event => onSelectVersion(event.target.value)}
                aria-label="Switch artifact version"
              >
                {book.versions
                  .slice()
                  .reverse()
                  .map(version => (
                    <option key={version.id} value={version.id}>
                      {versionMenuTitle(
                        version,
                        version.id === getProjectBookLatestVersion(book)?.id
                      )}
                    </option>
                  ))}
              </select>
              <ChevronDown className="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-muted-foreground" />
            </label>
          </div>

          <section className="flex h-[184px] items-center justify-center rounded-lg border border-black/[0.08] bg-white/70 p-4 dark:bg-white/[0.04]">
            {previewLoading ? (
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            ) : (
              <BookPreview
                version={selectedVersion}
                file={selectedFile}
                previewText={previewText}
                presentation="detail"
              />
            )}
          </section>

          <section className="mt-5 overflow-hidden rounded-lg border border-black/[0.08] bg-white/70 px-4 py-2 dark:bg-white/[0.04]">
            {isWebsite ? (
              <>
                <WebsiteStatusRow
                  status={siteStatus}
                  siteURL={siteURL}
                  onOpenSite={() => {
                    if (siteURL) {
                      window.open(siteURL, '_blank', 'noopener,noreferrer');
                    }
                  }}
                />
                {siteURL ? (
                  <DetailRow
                    icon={<Globe className="size-[13px]" />}
                    text={siteURL}
                    trailing={<ArrowUpRight className="size-[13px]" />}
                    onClick={() => {
                      window.open(siteURL, '_blank', 'noopener,noreferrer');
                    }}
                  />
                ) : (
                  <DetailRow
                    icon={<Globe className="size-[13px]" />}
                    text="No deployment URL"
                    muted
                  />
                )}
              </>
            ) : (
              <>
                <DetailRow
                  icon={
                    <BookKindIcon
                      kind={getBookVersionArtifactSystemIcon(selectedVersion)}
                      className="size-[13px]"
                    />
                  }
                  text={`${getBookVersionArtifactKindLabel(selectedVersion)} artifact`}
                  muted
                />
                {selectedFile ? (
                  <DetailRow
                    icon={<ArrowUpRight className="size-[13px]" />}
                    text={`${getBookFileName(selectedFile)} · ${formatBytes(selectedFile.size)}`}
                    trailing={<ArrowUpRight className="size-[13px]" />}
                    onClick={() => onOpenFile(selectedFile)}
                  />
                ) : null}
              </>
            )}

            {primaryConversationLink ? (
              <DetailRow
                icon={<MessageCircle className="size-[13px]" />}
                text={
                  openingConversation
                    ? 'Opening related chat'
                    : 'Open related chat'
                }
                trailing={
                  openingConversation ? (
                    <Loader2 className="size-[13px] animate-spin" />
                  ) : (
                    <ArrowUpRight className="size-[13px]" />
                  )
                }
                onClick={
                  openingConversation
                    ? undefined
                    : () => onOpenConversation(primaryConversationLink)
                }
              />
            ) : (
              <DetailRow
                icon={<MessageCircle className="size-[13px]" />}
                text="No related chat"
                muted
              />
            )}
            {relatedChatError ? (
              <div className="px-1 pb-2 text-xs text-muted-foreground">
                {relatedChatError}
              </div>
            ) : null}
          </section>

          {selectedVersion && selectedVersion.files.length > 1 ? (
            <section className="mt-5 overflow-hidden rounded-lg border border-black/[0.08] bg-white/70 dark:bg-white/[0.04]">
              <div className="border-b border-black/[0.08] px-4 py-2 text-[13px] font-semibold text-muted-foreground">
                Files
              </div>
              <div className="p-2">
                {selectedVersion.files.map(file => (
                  <button
                    key={file.id}
                    type="button"
                    className={cn(
                      'flex min-h-9 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
                      selectedFile?.id === file.id &&
                        'bg-black/[0.045] dark:bg-white/[0.06]'
                    )}
                    onClick={() => onSelectFile(file.id)}
                  >
                    <BookKindIcon
                      kind={getBookFileIconKind(file)}
                      className="size-[13px] text-muted-foreground"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {getBookFileName(file)}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {getBookFileKindLabel(file)}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          {selectedVersion ? (
            <div className="mt-4 text-xs text-muted-foreground">
              {versionIsLatest ? 'Latest version' : selectedVersion.label} ·{' '}
              {relativeChangeTime(getBookVersionModifiedAt(selectedVersion))}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

export function BookPage() {
  const navigate = useNavigate();
  const [books, setBooks] = useState<ProjectBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeBookID, setActiveBookID] = useState<string | null>(null);
  const [selectedVersionID, setSelectedVersionID] = useState<string | null>(
    null
  );
  const [selectedFileID, setSelectedFileID] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [openingConversationSessionID, setOpeningConversationSessionID] =
    useState<string | null>(null);
  const [relatedChatError, setRelatedChatError] = useState('');

  const activeBook = useMemo(
    () => books.find(book => book.id === activeBookID) ?? null,
    [activeBookID, books]
  );
  const selectedVersion = useMemo(
    () => selectedVersionFor(activeBook, selectedVersionID),
    [activeBook, selectedVersionID]
  );
  const selectedFile = useMemo(
    () => selectedFileFor(selectedVersion, selectedFileID),
    [selectedFileID, selectedVersion]
  );

  const selectBook = useCallback((book: ProjectBook) => {
    const latestVersion = getProjectBookLatestVersion(book);
    const primaryFile = getPrimaryBookFile(latestVersion);
    setActiveBookID(book.id);
    setSelectedVersionID(latestVersion?.id ?? null);
    setSelectedFileID(primaryFile?.id ?? null);
    setPreviewText('');
    setRelatedChatError('');
  }, []);

  const handleOpenConversation = useCallback(
    async (link: ProjectBookVersionSessionLink) => {
      const sessionID = link.sessionID.trim();
      if (!sessionID || openingConversationSessionID) {
        return;
      }

      setRelatedChatError('');
      setOpeningConversationSessionID(sessionID);
      try {
        const conversationTarget =
          await resolveConversationTargetForRuntimeSession(sessionID);
        if (!conversationTarget) {
          setRelatedChatError(
            'Related chat is not available in this workspace.'
          );
          return;
        }
        navigate(
          chatConversationPath(conversationTarget.conversationID, {
            agentGroupId: conversationTarget.agentGroupID,
          })
        );
      } catch {
        setRelatedChatError('Failed to open related chat.');
      } finally {
        setOpeningConversationSessionID(null);
      }
    },
    [navigate, openingConversationSessionID]
  );

  const loadPage = useCallback(
    async (options?: { silent?: boolean }) => {
      if (options?.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        await getWebsiteProfile();
        const agentBooks = await listAgentBooks(100);
        const nextBooks = projectBooksFromAgentBooks(agentBooks);
        const requestedBookID =
          new URLSearchParams(window.location.search).get('book') ??
          new URLSearchParams(window.location.search).get('item');
        setBooks(nextBooks);
        setActiveBookID(current => {
          if (current && nextBooks.some(book => book.id === current)) {
            return current;
          }
          if (requestedBookID) {
            const requested = nextBooks.find(
              book => book.id === requestedBookID
            );
            if (requested) {
              const latestVersion = getProjectBookLatestVersion(requested);
              setSelectedVersionID(latestVersion?.id ?? null);
              setSelectedFileID(getPrimaryBookFile(latestVersion)?.id ?? null);
              return requested.id;
            }
          }
          return null;
        });
      } catch (err) {
        if (getErrorStatus(err) === 401) {
          navigate(`/login?next=${encodeURIComponent('/book')}`, {
            replace: true,
          });
          return;
        }
        setError(getErrorMessage(err, 'Failed to load Books'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!selectedVersion) {
      setSelectedVersionID(null);
      setSelectedFileID(null);
      return;
    }
    if (!selectedVersionID || selectedVersion.id !== selectedVersionID) {
      setSelectedVersionID(selectedVersion.id);
    }
    const nextFile = selectedFileFor(selectedVersion, selectedFileID);
    if (nextFile?.id !== selectedFileID) {
      setSelectedFileID(nextFile?.id ?? null);
    }
  }, [selectedFileID, selectedVersion, selectedVersionID]);

  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      setPreviewText('');
      if (!selectedFile || !isPreviewableTextFile(selectedFile)) {
        setPreviewLoading(false);
        return;
      }
      setPreviewLoading(true);
      try {
        const text = await readPreviewText(selectedFile.path);
        if (!cancelled) {
          setPreviewText(text);
        }
      } catch {
        if (!cancelled) {
          setPreviewText('');
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    }
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [selectedFile]);

  if (activeBook) {
    return (
      <div className="h-[100dvh] overflow-hidden text-foreground">
        <DetailView
          book={activeBook}
          selectedVersion={selectedVersion}
          selectedFile={selectedFile}
          previewText={previewText}
          previewLoading={previewLoading}
          openingConversationSessionID={openingConversationSessionID}
          relatedChatError={relatedChatError}
          onBack={() => setActiveBookID(null)}
          onSendToChat={() => navigate('/chat')}
          onSelectVersion={versionID => {
            const version =
              activeBook.versions.find(item => item.id === versionID) ?? null;
            setSelectedVersionID(version?.id ?? null);
            setSelectedFileID(getPrimaryBookFile(version)?.id ?? null);
            setPreviewText('');
          }}
          onSelectFile={fileID => {
            setSelectedFileID(fileID);
            setPreviewText('');
          }}
          onOpenFile={file => {
            window.open(
              buildAgentFileURL(file.path),
              '_blank',
              'noopener,noreferrer'
            );
          }}
          onOpenConversation={link => {
            void handleOpenConversation(link);
          }}
        />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-hidden text-foreground">
      <GalleryView
        books={books}
        loading={loading}
        error={error}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        onRefresh={() => {
          void loadPage({ silent: true });
        }}
        refreshing={refreshing}
        onOpenBook={selectBook}
      />
    </div>
  );
}
